import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import apiClient from '../api/api';
import loadImage from '../assets/imageLoader';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import { 
    FaSearch, FaUserCircle, FaSignOutAlt, FaTachometerAlt, 
    FaWallet, FaExchangeAlt, FaBars, FaTimes 
} from 'react-icons/fa';
import NotificationsDropdown from './NotificationsDropdown';

function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [hasNewAlert, setHasNewAlert] = useState(false);
  
  const navigate = useNavigate();
  const profileDropdownRef = useRef(null);
  
  const { isAuthenticated, user, logout, loading, refetchUser } = useAuth();
  const { socket, unreadMessageCount } = useSocket();
  
  useEffect(() => {
    if (socket) {
        const handleNotif = () => setHasNewAlert(true);
        socket.on('new_notification', handleNotif);
        return () => socket.off('new_notification', handleNotif);
    }
  }, [socket]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setIsProfileDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [profileDropdownRef]);

  const handleSearch = () => {
    if (searchTerm.trim()) {
      navigate(`/search?q=${searchTerm.trim()}`);
      setIsMenuOpen(false);
    }
  };

  const handleDashboardClick = () => {
    setIsProfileDropdownOpen(false);
    let path = '/';
    switch (user?.role) {
      case 'buyer': path = '/owner-dashboard'; break;
      case 'worker': path = '/worker-dashboard'; break;
      case 'broker': path = '/broker-dashboard'; break;
      case 'admin': path = '/admin'; break;
      default: path = `/profile/${user.userId}`; break;
    }
    navigate(path);
  };

  const handleSwitchProfile = async () => {
    setIsProfileDropdownOpen(false);
    try {
        const res = await apiClient.post('/auth/switch-role');
        const data = res.data;
        if (data.token) localStorage.setItem('token', data.token);
        await refetchUser();
        if (data.incomplete) {
            navigate('/complete-profile');
        } else {
            const path = data.role === 'worker' ? '/worker-dashboard' : '/owner-dashboard';
            navigate(path);
            window.location.reload(); 
        }
    } catch (err) {
        console.error(err);
        alert("Failed to switch profile.");
    }
  };

  const handleLogout = async () => {
    setIsProfileDropdownOpen(false);
    await logout(navigate);
  };

  if (loading) {
    return (
      <header className="bg-white/80 backdrop-blur-md sticky top-0 z-[100] border-b border-slate-100">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center animate-pulse">
          <div className="h-8 bg-slate-200 rounded-lg w-32"></div>
          <div className="h-10 bg-slate-200 rounded-full w-1/3 hidden md:block"></div>
          <div className="h-10 bg-slate-200 rounded-full w-24"></div>
        </div>
      </header>
    );
  }

  const targetRoleLabel = user?.role === 'worker' ? 'Buyer' : 'Worker';

  return (
    <header className="bg-white/80 backdrop-blur-md sticky top-0 z-[100] border-b border-slate-100 font-sans">
      <div className="container mx-auto px-6 py-4 flex justify-between items-center gap-8">
        
        {/* Logo */}
        <Link to="/" className="flex items-center space-x-2 shrink-0 group">
          <div className="w-8 h-8 md:w-10 md:h-10 bg-emerald-600 rounded-xl flex items-center justify-center text-white font-black text-lg md:text-xl shadow-lg group-hover:rotate-6 transition-transform">S</div>
          <span className="text-xl md:text-2xl font-black text-slate-800 tracking-tighter">Service<span className="text-emerald-600">24</span></span>
        </Link>

        {/* Desktop Search */}
        <div className="hidden md:flex flex-grow max-w-xl ml-8">
          <div className="relative w-full group">
            <input
              type="text"
              placeholder="Search for skills, jobs, or people..."
              className="w-full bg-slate-100 border-none rounded-2xl py-3 pl-12 pr-4 text-sm font-medium focus:ring-2 ring-emerald-500/50 outline-none transition-all placeholder:text-slate-400"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-emerald-600 transition-colors" />
          </div>
        </div>

        {/* Nav Links & User Section */}
        <div className="flex items-center space-x-4">
          <nav className="hidden lg:flex items-center space-x-6 mr-4">
            <Link to="/" className="text-sm font-bold text-slate-600 hover:text-emerald-600 transition-colors">Home</Link>
            <Link to="/jobs" className="text-sm font-bold text-slate-600 hover:text-emerald-600 transition-colors">Browse Jobs</Link>
            <Link to="/contact" className="text-sm font-bold text-slate-600 hover:text-emerald-600 transition-colors">Support</Link>
          </nav>

          {isAuthenticated ? (
            <div className="flex items-center space-x-2 md:space-x-4">
              <div className="hidden md:block relative" onClick={() => setHasNewAlert(false)}>
                <NotificationsDropdown />
                {hasNewAlert && (
                    <div className="absolute top-0 right-0 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white animate-pulse"></div>
                )}
              </div>
              <div className="relative" ref={profileDropdownRef}>
                <button
                  onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                  className="flex items-center group p-1 relative"
                >
                  <img
                    src={loadImage(user?.picture)}
                    alt="Profile"
                    className="w-9 h-9 md:w-10 md:h-10 rounded-xl object-cover ring-2 ring-transparent group-hover:ring-emerald-500 transition-all shadow-md"
                  />
                  {(hasNewAlert || unreadMessageCount > 0) && (
                      <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white animate-pulse"></div>
                  )}
                </button>
                
                {isProfileDropdownOpen && (
                  <div className="absolute right-0 mt-3 w-64 bg-white rounded-2xl shadow-2xl border border-slate-100 py-3 z-50 animate-fade-in">
                    <div className="px-4 py-3 border-b border-slate-50 mb-2">
                        <p className="text-xs font-black text-slate-400 uppercase tracking-widest">Signed in as</p>
                        <p className="font-bold text-slate-800 truncate">{user?.name}</p>
                        {user?.role === 'admin' && (
                            <span className="text-[9px] font-black bg-red-50 text-red-600 px-2 py-0.5 rounded uppercase mt-1 inline-block">System Administrator</span>
                        )}
                    </div>
                    
                    {user?.role === 'admin' ? (
                        <>
                            <button onClick={handleDashboardClick} className="w-full flex items-center space-x-3 px-4 py-2.5 text-sm font-bold text-slate-600 hover:bg-slate-50 hover:text-red-600 transition-all">
                                <FaTachometerAlt className="opacity-50" /> <span>Admin Overview</span>
                            </button>
                        </>
                    ) : (
                        <>
                            <Link 
                                to={user?.role === 'buyer' ? '/owner-profile' : '/worker-dashboard/profile'} 
                                onClick={() => setIsProfileDropdownOpen(false)}
                                className="w-full flex items-center space-x-3 px-4 py-2.5 text-sm font-bold text-slate-600 hover:bg-slate-50 hover:text-emerald-600 transition-all"
                            >
                                <FaUserCircle className="opacity-50" /> <span>My Profile</span>
                            </Link>

                            <button onClick={handleDashboardClick} className="w-full flex items-center space-x-3 px-4 py-2.5 text-sm font-bold text-slate-600 hover:bg-slate-50 hover:text-emerald-600 transition-all">
                                <FaTachometerAlt className="opacity-50" /> <span>Dashboard</span>
                            </button>
                            
                            {user?.role !== 'admin' && (
                                <button onClick={handleSwitchProfile} className="w-full flex items-center space-x-3 px-4 py-2.5 text-sm font-bold text-blue-600 hover:bg-blue-50 transition-all">
                                    <FaExchangeAlt className="opacity-50" /> <span>Switch to {targetRoleLabel}</span>
                                </button>
                            )}
                            
                            <Link to="/wallet" onClick={() => setIsProfileDropdownOpen(false)} className="flex items-center space-x-3 px-4 py-2.5 text-sm font-bold text-slate-600 hover:bg-slate-50 hover:text-emerald-600 transition-all">
                                <FaWallet className="opacity-50" /> <span>Wallet</span>
                            </Link>
                        </>
                    )}
                    
                    <div className="h-px bg-slate-50 my-2"></div>
                    
                    <button onClick={handleLogout} className="w-full flex items-center space-x-3 px-4 py-2.5 text-sm font-bold text-red-500 hover:bg-red-50 transition-all">
                        <FaSignOutAlt className="opacity-50" /> <span>Logout</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center space-x-2 md:space-x-3">
              <Link to="/login" className="hidden sm:block text-sm font-bold text-slate-600 hover:text-slate-900 px-4 py-2">Sign In</Link>
              <Link to="/register" className="bg-slate-900 text-white px-5 md:px-6 py-2.5 rounded-xl text-xs md:text-sm font-bold shadow-lg hover:bg-slate-800 transition-all">Join Service24</Link>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

export default Header;
